import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'
import { FormControl, FormGroup } from '@angular/forms'
import { forkJoin, map, Observable, of, startWith, switchMap, take, tap } from 'rxjs'
import { HttpErrorResponse } from '@angular/common/http'
import { TemplateService } from './template.service'
import { ContentUnitService } from '@features/content-unit/services/content-unit.service'
import { TemplateFormService } from './template-form.service'
import { ModalHelperService } from '@services/modal-helper.service'
import { GlobalErrorService } from '@services/global-error.service'
import { TemplateApiResponse, TemplateContentUnit, TemplateForm } from '../models/template.model'
import { ContentState } from '@core/utils/route-utils'
import { TemplateSidebarBridgeService } from './template-sidebar-bridge.service'
import { ContentUnitApiResponse } from '@features/content-unit/models/content-unit.model'
import { TemplateStateService } from '@app/layout/sidebar/services/template-state.service'

@Injectable()
export class TemplateManagerService {
  // Component State Signals
  public readonly contentState = signal<ContentState>('none')
  public readonly isReadOnly = signal(false)
  public readonly suggestedTags = signal<string[]>([])
  public readonly templateForm: FormGroup<TemplateForm>

  // Computed Signals for derived state
  public readonly isViewMode = computed(() => this.contentState() === 'view')
  public readonly isEditMode = computed(() => this.contentState() === 'edit')
  public readonly isCreateMode = computed(() => this.contentState() === 'create')

  private readonly route = inject(ActivatedRoute)
  private readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)
  private readonly templateService = inject(TemplateService)
  private readonly contentUnitService = inject(ContentUnitService)
  private readonly formService = inject(TemplateFormService)
  private readonly globalErrorService = inject(GlobalErrorService)
  private readonly modalHelper = inject(ModalHelperService)
  private readonly templateState = inject(TemplateStateService)
  private readonly bridgeService = inject(TemplateSidebarBridgeService)

  constructor() {
    this.templateForm = this.formService.buildForm()
    this.loadInitialData()
    this.setupBridgeSync()
  }

  public loadTemplate(uuid: string): void {
    this.templateService.getById(uuid).pipe(
      take(1),
      switchMap(data => {
        if (data.contentUnits.length === 0) {
          return of({ data, fullCUs: [] })
        }
        const requests = [...data.contentUnits]
          .sort((a, b) => a.position - b.position)
          .map(cu =>
            this.contentUnitService.getById(cu.uuid).pipe(
              map(fullCU => ({
                contentUnit: fullCU,
                condition: cu.condition,
                position: cu.position
              }))
            )
          )
        return forkJoin(requests).pipe(
          map(fullCUs => ({ data, fullCUs }))
        )
      })
    ).subscribe({
      next: ({ data, fullCUs }) => {
        const newControls = fullCUs.map(cu => new FormControl<TemplateContentUnit | null>(cu))
        this.templateForm.setControl('contentUnits', this.formService.createFormArray(newControls))

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { contentUnits, ...rest } = data
        this.templateForm.patchValue(rest)
        this.templateForm.markAsPristine()
        this.formService.addAsyncValidators(
          this.templateForm,
          data.title,
          data.certificateCategory,
          data.coverageType
        )
      },
      error: () => this.globalErrorService.reportError({
        subsystemName: 'Template Loading',
        description: 'The Template could not be loaded.'
      })
    })
  }

  public searchTags(query: string): void {
    this.templateService.getTags(query).pipe(take(1)).subscribe(tags => {
      if (tags.some(t => t.toLowerCase() === query.toLowerCase())) {
        this.suggestedTags.set(tags)
      } else {
        this.suggestedTags.set([query, ...tags])
      }
    })
  }

  public save(): void {
    if (this.templateForm.invalid) {
      this.templateForm.markAllAsTouched()
      return
    }

    this.performSave$().subscribe({
      error: (error: unknown) => this.handleSaveError(error)
    })
  }

  public confirmLeave(): Observable<boolean> {
    return this.modalHelper.confirmLeave(() => {
      if (this.templateForm.invalid) {
        this.templateForm.markAllAsTouched()
        return of(false)
      }
      return this.performSave$()
    }, (error) => this.handleSaveError(error))
  }

  public cancel(): void {
    if (this.isCreateMode()) {
      void this.router.navigate(['/template'])
    } else {
      const uuid = this.templateForm.controls.uuid.value
      if (uuid !== undefined && uuid !== null && uuid !== '') {
        this.isReadOnly.set(true)
        this.contentState.set('view')
        this.loadTemplate(uuid)
      } else {
        void this.router.navigate(['/template'])
      }
    }
  }

  public performSave$(): Observable<TemplateApiResponse> {
    const data = this.templateForm.getRawValue()
    const apiData: TemplateApiResponse = {
      uuid: data.uuid,
      title: data.title ?? '',
      description: data.description ?? '',
      tags: data.tags ?? [],
      certificateCategory: data.certificateCategory ?? '',
      coverageType: data.coverageType ?? '',
      contentUnits: (data.contentUnits ?? []).map((cu, index) => ({
        uuid: cu?.contentUnit.uuid ?? '',
        condition: cu?.condition ?? '',
        position: cu?.position ?? index
      }))
    }

    return this.templateService.save(apiData).pipe(
      take(1),
      tap((savedTemplate) => {
        this.templateForm.markAsPristine()
        if (this.isCreateMode()) {
          void this.router.navigate(['/template', savedTemplate.uuid])
          this.templateState.reload().subscribe()
        } else {
          this.isReadOnly.set(true)
          this.contentState.set('view')
          if (savedTemplate.uuid !== undefined && savedTemplate.uuid !== null && savedTemplate.uuid !== '') {
            this.loadTemplate(savedTemplate.uuid)
          }
        }
      })
    )
  }

  public confirmDelete(): Observable<boolean> {
    return this.modalHelper.confirmDelete(
      'Delete Template',
      `Are you sure you want to delete template ${this.templateForm.getRawValue().title}`
    )
  }

  public duplicate(): void {
    this.templateForm.patchValue({ uuid: null })
    this.formService.addAsyncValidators(this.templateForm)
    this.isReadOnly.set(false)
    this.contentState.set('create')
  }

  public close(): void {
    void this.router.navigate(['/template'])
  }

  public delete(): void {
    this.confirmDelete().subscribe(confirmed => {
      if (confirmed) {
        this.templateService.delete(this.templateForm.getRawValue().uuid!).subscribe({
          next: () => {
            void this.router.navigate(['/template'])
            this.templateState.reload().subscribe()
          },
          error: () => this.globalErrorService.reportError({
            subsystemName: 'Template Deletion',
            description: 'Could not delete template. Please try again.'
          })
        })
      }
    })
  }

  private handleSaveError(error: unknown): void {
    let description = 'The Template could not be saved.'
    if (error instanceof HttpErrorResponse && error.status === 409) {
      description = 'The combination of Certificate Category and Coverage Type is already in use.'
    }

    this.globalErrorService.reportError({
      subsystemName: 'Template Saving',
      description
    })
  }

  private loadInitialData(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const uuid = params['uuid'] as string | undefined
      if (typeof uuid === 'string' && uuid !== '') {
        this.isReadOnly.set(true)
        this.loadTemplate(uuid)
      } else {
        this.isReadOnly.set(false)
        this.templateForm.reset()
        this.formService.addAsyncValidators(this.templateForm)
      }
    })
  }

  private setupBridgeSync(): void {
    effect(() => {
      this.bridgeService.isTemplateEditMode.set(this.isEditMode() || this.isCreateMode())
    })

    this.templateForm.valueChanges.pipe(
      startWith(this.templateForm.value),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const contentUnits = this.templateForm.controls.contentUnits.value
      const uuids = contentUnits
        .map(cu => cu?.contentUnit?.uuid)
        .filter((uuid): uuid is string => typeof uuid === 'string' && uuid !== '')
      this.bridgeService.usedContentUnitUuids.set(uuids)
    })

    this.bridgeService.addContentUnit$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(cuItem => {
      this.contentUnitService.getById(cuItem.uuid).pipe(take(1)).subscribe(fullCu => {
        this.addContentUnitToTemplate(fullCu)
      })
    })

    this.destroyRef.onDestroy(() => {
      this.bridgeService.isTemplateEditMode.set(false)
      this.bridgeService.usedContentUnitUuids.set([])
    })
  }

  private addContentUnitToTemplate(cu: ContentUnitApiResponse): void {
    const contentUnits = this.templateForm.controls.contentUnits
    const newEntry: TemplateContentUnit = {
      contentUnit: cu,
      condition: '',
      position: contentUnits.length
    }
    contentUnits.push(new FormControl<TemplateContentUnit | null>(newEntry))
    this.templateForm.markAsDirty()
  }
}
